import { Empty, Spin, notification } from 'antd'
import { useFormatData } from 'hooks'
import { t } from 'i18next'
import { IMedicineExtend, IUsageMedicine } from 'interfaces'
import { IMedicineMho } from 'interfaces/medicine'
import { IInventoryQuerySearch } from 'pages/repoInventory/interfaces'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMedicineService } from 'services'
import styled from 'styled-components'
import { StyledAutoComplete } from 'styles/styledGlobal'
import { debounce } from 'utils'

const StyledOptionItem = styled.div<{ $disabled?: boolean }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  ${({ $disabled }) =>
    $disabled
      ? `
      pointer-events: none;
      opacity: 0.5;
      cursor: default;
  `
      : ''}
`
interface IProps {
  medicinesSelected?: Array<any>
  className?: string
  isSearchMoh?: boolean
  isClosedWhenSelected?: boolean
  disabledNoAmount?: boolean
  onChange?: (medicines: Array<any>, medicine?: any) => void
}

const SearchMedicine = (props: IProps) => {
  const {
    medicinesSelected,
    onChange,
    className,
    disabledNoAmount,
    isSearchMoh,
    isClosedWhenSelected
  } = props
  const { getNumberCommasFixed } = useFormatData()
  const [medicine, setMedicine] = useState(null)
  const medicinesRef = useRef<Array<IMedicineExtend | any>>([])
  const [options, setOptions] = useState<Array<any>>([])
  const [loading, setLoading] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const { searchMedicineMoh, searchMedicine } = useMedicineService()

  useEffect(() => {
    handleOnSearch('')
  }, [])

  useEffect(() => {
    if (isSearchMoh) {
      buildOptionsMoh()
    } else {
      buildOptions()
    }
  }, [medicinesSelected])

  const handleOnSearch = (keySearch?: string) => {
    if (keySearch && keySearch?.length < 3) {
      return
    }
    setLoading(true)
    handleOnSearchDebounce(keySearch)
  }

  const handleOnSearchDebounce = useCallback(
    debounce((keySearch: string) => {
      const query: IInventoryQuerySearch = {
        query: {
          q: keySearch
        }
      }
      handleSearchMedicine(query)
    }, 1000),
    [medicinesSelected]
  )

  const handleSearchMedicine = (query: IInventoryQuerySearch) => {
    const httpSearch = isSearchMoh ? searchMedicineMoh : searchMedicine
    httpSearch(query)
      .then((res: any) => {
        medicinesRef.current = res?.data
        if (isSearchMoh) {
          buildOptionsMoh()
        } else {
          buildOptions()
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const buildOptionsMoh = () => {
    const options = (medicinesRef.current as Array<IMedicineMho>)
      ?.filter((item) => {
        return !medicinesSelected?.find((me) => me?.id === item?.id)
      })
      ?.map((item) => {
        return {
          label: (
            <StyledOptionItem>
              <span>{`${item?.name} - ${item?.manufacturer}`}</span>
            </StyledOptionItem>
          ),
          value: item?.id
        }
      })
    setOptions(options)
  }

  const buildOptions = () => {
    const options = medicinesRef.current
      ?.filter((item) => {
        return !medicinesSelected?.find((me) => me.code === item.code)
      })
      ?.map((item) => {
        return {
          label: (
            <StyledOptionItem $disabled={!item?.total && !!disabledNoAmount}>
              <span>{`${item?.name} (${item?.code}-${item.concentration})`}</span>
              <span>{`${getNumberCommasFixed(item?.total) || 0}`}</span>
            </StyledOptionItem>
          ),
          value: item?.code
        }
      })
    setOptions(options)
  }

  const handleAddMedicine = (codeMedicine?: any) => {
    let medicineSelected = medicinesRef.current?.find(
      (item) => item[isSearchMoh ? 'id' : 'code'] === codeMedicine
    )
    if (isClosedWhenSelected && onChange) {
      onChange([medicineSelected])
      return
    }
    setMedicine(null)
    if (!medicineSelected?.total && disabledNoAmount) {
      notification.error({ message: t('medicine-no-more') })
      return
    }
    if (medicineSelected) {
      medicineSelected = {
        ...medicineSelected,
        usage: medicineSelected?.usage,
        quantity: getQuantity(medicineSelected?.usage),
        totalAmount:
          getQuantity(medicineSelected?.usage) *
          Number(medicineSelected?.price || 0)
      }
    }

    if (onChange) {
      onChange(
        [...(medicinesSelected || []), medicineSelected],
        medicineSelected
      )
    }
  }

  const getQuantity = (usage?: IUsageMedicine) => {
    if (!usage?.details) return 0
    return usage?.details?.reduce(
      (accumulator, currentValue) => accumulator + Number(currentValue?.dose),
      0
    )
  }
  const handleDropdownVisibleChange = (open: any) => {
    if (!open) {
      setShowOptions(open)
    }
  }

  const handleOnClick = () => {
    setShowOptions(isClosedWhenSelected ? !showOptions : true)
  }

  return (
    <StyledAutoComplete
      showSearch
      className={className}
      open={showOptions}
      value={medicine}
      onSearch={handleOnSearch}
      onChange={handleAddMedicine}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      onClick={handleOnClick}
      placeholder={t('search')}
      options={options}
      suffixIcon={<img src="/images/ic-search.svg" alt="" />}
      notFoundContent={
        loading ? <Spin size="small" /> : <Empty description={t('no-data')} />
      }
      loading={loading}
    />
  )
}

export default SearchMedicine
